import React, { useState, useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import { getBranchMeta, getInitials, clampInbox } from '../utils/format';
import styles from './OrgTree.module.css';

function AgentNode({ agent, isSelected, onClick }) {
  const branchMeta = getBranchMeta(agent.branch);
  const initials = getInitials(agent.name);

  return (
    <button
      type="button"
      className={`${styles.agentNode} ${isSelected ? styles.agentNodeSelected : ''}`}
      onClick={() => onClick(agent.id)}
      aria-selected={isSelected}
    >
      <div
        className={styles.agentAvatar}
        style={{
          backgroundColor: `${branchMeta.color}20`,
          color: branchMeta.color,
        }}
      >
        {initials}
      </div>
      <div className={styles.agentInfo}>
        <div className={styles.agentNameRow}>
          <span className={styles.agentName}>{agent.name}</span>
          <StatusBadge status={agent.status} />
        </div>
        <span className={styles.agentRole}>{agent.role_title}</span>
      </div>
      {agent.inbox_count > 0 && (
        <span className={styles.agentInbox}>{clampInbox(agent.inbox_count)}</span>
      )}
    </button>
  );
}

function BranchNode({ branch, agents, selectedId, onSelect, defaultExpanded = true }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const branchMeta = getBranchMeta(branch.name);

  return (
    <div className={styles.branch}>
      <button
        type="button"
        className={styles.branchHeader}
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        aria-label={`${branch.name} branch, ${agents.length} agents`}
      >
        <span
          className={styles.branchIndicator}
          style={{ backgroundColor: branchMeta.color }}
        />
        <span className={styles.branchName} style={{ color: branchMeta.color }}>
          {branch.name}
        </span>
        <ChevronRight
          size={16}
          className={`${styles.branchToggle} ${expanded ? styles.branchToggleOpen : ''}`}
        />
      </button>
      <div
        className={`${styles.branchChildren} ${expanded ? styles.branchChildrenOpen : ''}`}
      >
        <div className={styles.branchLine} />
        <ul className={styles.branchList}>
          {agents.map((agent) => (
            <li key={agent.id}>
              <AgentNode
                agent={agent}
                isSelected={selectedId === agent.id}
                onClick={onSelect}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function OrgTree({ agents, branches, selectedId, onSelect }) {
  const agentsByBranch = useMemo(() => {
    const map = new Map();
    branches.forEach((b) => map.set(b.id, []));
    agents.forEach((a) => {
      const list = map.get(a.branch) || [];
      list.push(a);
      map.set(a.branch, list);
    });
    return map;
  }, [agents, branches]);

  return (
    <nav className={styles.orgTree} aria-label="Organization tree" role="tree">
      {branches.map((branch) => (
        <BranchNode
          key={branch.id}
          branch={branch}
          agents={agentsByBranch.get(branch.id) || []}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      ))}
    </nav>
  );
}
